import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import { API_BASE } from './api-base';

/**
 * Resolves the API origin (scheme + host) from API_BASE so it can be added to
 * `tracePropagationTargets`, linking a frontend action to its backend request
 * in Sentry (distributed tracing).
 *
 * API_BASE is either a relative path (local dev, `/api`, proxied by Vite —
 * same-origin, already covered by the browser SDK's same-origin default) or
 * an absolute cross-origin URL (staging/production, e.g.
 * `https://api.railway.app/api`). Returns undefined when resolution fails
 * (should not happen for either shape) rather than throwing at init time.
 */
function resolveApiOrigin(): string | undefined {
  try {
    return new URL(API_BASE, window.location.origin).origin;
  } catch {
    return undefined;
  }
}

export function initSentry(): void {
  const dsn = import.meta.env['VITE_SENTRY_DSN'] as string | undefined;
  if (!dsn) return;

  const apiOrigin = resolveApiOrigin();

  Sentry.init({
    dsn,
    environment: import.meta.env['MODE'],
    release: import.meta.env['VITE_SENTRY_RELEASE'] as string | undefined,
    integrations: [browserTracingIntegration()],
    // Capture 100% of transactions — tune down if volume is too high.
    tracesSampleRate: 1.0,
    // Links a frontend action to its backend request in Sentry (distributed
    // tracing). Undefined falls back to the browser SDK's same-origin default.
    tracePropagationTargets: apiOrigin ? [apiOrigin] : undefined,
  });
}

/**
 * Sets (or clears) the Sentry user context. Safe to call even when Sentry was
 * never initialised (VITE_SENTRY_DSN unset) — Sentry.setUser is a no-op
 * without an active client.
 *
 * Wire this at every point the authenticated user changes: login, logout,
 * and token refresh. See `src/lib/auth-client.ts` for the reference wiring.
 */
export function setSentryUser(user: { id: number; email: string } | null): void {
  Sentry.setUser(user ? { id: String(user.id), email: user.email } : null);
}

/**
 * Records a Sentry breadcrumb for a business action (not a raw DOM event),
 * e.g. a mutation starting, succeeding, or failing. Safe to call even when
 * Sentry was never initialised — Sentry.addBreadcrumb is a no-op without an
 * active client.
 *
 * Prefer the `withMutationBreadcrumbs` wrapper (`packages/screens/src/lib/mutation-breadcrumbs.ts`)
 * over calling this directly from a mutation hook — it wires start/success/error
 * consistently. `ScreensProvider` routes @ooh/screens mutations here through
 * `onActionBreadcrumb`.
 */
export function addActionBreadcrumb(
  message: string,
  level: 'info' | 'error' = 'info',
  data?: Record<string, unknown>,
): void {
  Sentry.addBreadcrumb({ category: 'action', message, level, data });
}

export { Sentry };
